import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Box, Button, Flex, Loading, Typography } from '@iheartjane/reefer';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';

import { kioskAuthenticationQueryKeys } from '@jane/kiosk/data-access';
import type { RootLoaderType } from '@jane/kiosk/data-access';
import {
  useKioskSpacing,
  useKioskUserSession,
  useShouldKioskShowGold,
  useShowJanePayOnKiosk,
  useTypedRouteLoaderData,
} from '@jane/kiosk/hooks';
import { useModalsDisplayedContext } from '@jane/kiosk/providers';
import { buildKioskMobileUrl } from '@jane/kiosk/util';
import { useGetStore } from '@jane/shared/data-access';
import type { Store } from '@jane/shared/types';

import { QRCodeCallout } from '../QRCodeCallout/QRCodeCallout';

export const LoginScreen = ({
  onClick,
  qrCode,
}: {
  onClick(): void;
  qrCode: string;
}) => {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const { defaultSpacing, getKioskSpacing, isIpad } = useKioskSpacing();
  const { setLoginModalDisplayed } = useModalsDisplayedContext();

  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const {
    deviceGrant,
    userName,
    signedIn,
    isLoading,
    isReplacingQRCode,
    qrCodeError,
  } = useKioskUserSession({
    store,
  });

  const janeGold = useShouldKioskShowGold();
  const janePay = useShowJanePayOnKiosk();
  const hideJanePayBranding = store?.jane_pay_integration?.white_label;

  const enableKioskQRCodeAuthWithExternalURL = useFlag(
    FLAGS.c3EnableKioskQRCodeAuthWithExternalURL
  );

  const onClose = () => {
    setLoginModalDisplayed(false);
    onClick();
  };

  const mobileLoginUrl = useMemo(() => {
    if (!deviceGrant?.user_code) {
      return undefined;
    }

    if (
      enableKioskQRCodeAuthWithExternalURL &&
      deviceGrant.external_authentication_url != null
    ) {
      const externalUrl = new URL(deviceGrant.external_authentication_url);
      externalUrl.searchParams.set('user_code', deviceGrant.user_code);
      externalUrl.searchParams.set('source', 'Checkout');

      return externalUrl.toString();
    }

    return buildKioskMobileUrl(storeId, 'login', {
      user_code: deviceGrant.user_code,
      source: 'Checkout',
    });
  }, [deviceGrant, pathname, storeId, enableKioskQRCodeAuthWithExternalURL]);

  useEffect(() => {
    if (signedIn) {
      queryClient.invalidateQueries({
        queryKey: kioskAuthenticationQueryKeys.kioskUser(),
      });
    }
  }, [signedIn, queryClient, kioskAuthenticationQueryKeys.kioskUser]);

  return isLoading ? (
    <Loading />
  ) : signedIn ? (
    <Flex
      flexDirection="column"
      alignItems="center"
      m={getKioskSpacing(32, 40)}
      width="432px"
    >
      <Typography
        variant="h2-semibold"
        mb={getKioskSpacing(24, 8)}
        width="inherit"
      >
        {userName?.includes('privaterelay.appleid.com')
          ? 'Welcome'
          : `${userName}, welcome`}{' '}
        to the kiosk at {store.name}
      </Typography>
      {(janePay || janeGold) && (
        <Typography mb={defaultSpacing}>
          Continue linking your bank on your mobile device to
          {janeGold && ' earn cash back with Jane Gold'}
          {janePay && janeGold && ' and'}
          {janePay &&
            ` pay${hideJanePayBranding ? '' : ' with Jane Pay'} at this kiosk`}
          .
        </Typography>
      )}
      <Box position="relative" mb={24}>
        <Loading color="black" size="lg" />
      </Box>
      <Button
        label="Cancel"
        onClick={onClose}
        full
        branded
        size={isIpad ? 'small' : 'default'}
      />
    </Flex>
  ) : (
    <QRCodeCallout
      buttonLabel="Back to checkout"
      onClick={onClose}
      qrCode={qrCode}
      type="login"
      url={isReplacingQRCode ? undefined : mobileLoginUrl}
      error={qrCodeError}
    />
  );
};
